import { useSearchParams } from 'react-router-dom';

import { Header } from '@/components';
import { Breadcrumb } from '@/components/Breadcrumb/Breadcrumb';

import { TAB_PARAM } from './components/settings-anchors';
import {
  SETTINGS_DRILL_PARAMS,
  type SettingsSection,
  useAvailableSettingsSections,
} from './components/settings-sections';
import { SettingsLayout } from './components/SettingsLayout';
import { useSettingsBreadcrumb } from './components/use-settings-breadcrumb';

export default function SettingsPage() {
  const sections = useAvailableSettingsSections();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabFromUrl = searchParams.get(TAB_PARAM) as SettingsSection | null;
  const hasOpenSection = sections.some((sec) => sec.value === tabFromUrl);
  const active: SettingsSection = hasOpenSection
    ? (tabFromUrl as SettingsSection)
    : (sections[0]?.value ?? 'dashboard');

  const openSection = (value: SettingsSection, params?: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(TAB_PARAM, value);
      // Drop any drill-down state from the previous section, then apply the
      // drill params for this navigation (e.g. a notification category).
      SETTINGS_DRILL_PARAMS.forEach((param) => next.delete(param));
      if (params) {
        Object.entries(params).forEach(([key, val]) => next.set(key, val));
      }
      return next;
    });
  };

  const closeSection = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(TAB_PARAM);
      SETTINGS_DRILL_PARAMS.forEach((param) => next.delete(param));
      return next;
    });
  };

  const { items, siblings, parent, backTo } = useSettingsBreadcrumb({
    sections,
    active,
    hasOpenSection,
    onOpen: openSection,
    onClose: closeSection,
  });

  return (
    <>
      <Header
        title={<Breadcrumb items={items} siblings={siblings} parent={parent} />}
        backButton={!!backTo}
        backButtonNavigateTo={backTo}
      />
      <SettingsLayout
        sections={sections}
        active={active}
        onChange={openSection}
        open={hasOpenSection}
      />
    </>
  );
}
